import fetchWithAuth from "./secureFetch";
import { getJobStatusManager, JobCallbacks } from "./jobStatusManager";

// Get the work processor API URL from environment variable
// Work Processor is a separate microservice with its own URL
const WORK_PROCESSOR_API_URL = import.meta.env.VITE_WORK_PROCESSOR_API_URL || "http://wpcluster.intelligenic.ai:50506";
const BACKEND_URI = import.meta.env.VITE_API_BASE_URL || "";
console.log("Work Processor API URL configured as:", WORK_PROCESSOR_API_URL);

// Get the appropriate URL based on environment
export const getWorkProcessorUrl = (path: string) => {
    // Always use the full URL from environment variable
    // No proxy needed - using direct connections
    return `${WORK_PROCESSOR_API_URL}${path}`;
};

export interface JobRequest {
    projectid: number;
    workproductid?: number;
    userid?: number;
    featureid?: number;
    user_story_id?: string; // UUID string, not a number
    force_new_version?: boolean;
}

export interface JobSubmitResponse {
    job_id: string;
    message: string;
    status: string;
}

export interface JobStreamUpdate {
    job_id: string;
    status: string;
    progress?: number;
    message?: string;
    timestamp?: number;
    result?: any;
    output?: any;
    current_section?: number;
    sections_completed?: number;
    total_sections?: number;
}

export interface JobStatus {
    jobid: string;
    status: string;
    progress?: number;
    message?: string;
    created_at?: string;
    updated_at?: string;
    completed_at?: string;
    description?: string;
    sections_completed?: number;
    total_sections?: number;
    error?: string | null;
}

/**
 * Submit a new job to the work processor
 */
export async function submitJobToProcessor(jobData: JobRequest, idToken: string): Promise<JobSubmitResponse> {
    // Intelligenic is the authoritative Product/RBAC boundary. Never call the
    // processor directly: authorization must complete before a job is queued.
    const url = `${BACKEND_URI}/api/jobprocessor/addjob`;

    // Transform the data to match Work Processor API expectations
    // Work Processor expects 'userstoryid' (no underscore), not 'user_story_id'
    // ✅ FIX: Only destructure user_story_id, keep featureid in rest
    const { user_story_id, ...rest } = jobData;
    const transformedData: any = { ...rest };
    // Only send userstoryid at story level. Product/feature-level submissions
    // omit it entirely, the same way featureid is omitted above story scope.
    if (user_story_id) {
        transformedData.userstoryid = user_story_id;
    }

    console.log("Original job data:", jobData);
    console.log("Transformed job data:", transformedData);
    console.log("user_story_id present?", user_story_id, "-> userstoryid:", transformedData.userstoryid);

    // Use fetchWithAuth which will add the authorization header
    console.log("About to call fetchWithAuth...");
    const response = await fetchWithAuth(url, idToken, {
        method: "POST",
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify(transformedData)
    });
    console.log("fetchWithAuth returned!");

    console.log("Response status:", response.status, response.statusText);
    console.log("Response headers:", Object.fromEntries(response.headers.entries()));

    if (!response.ok) {
        let errorMessage = `Failed to submit job: ${response.statusText}`;
        try {
            const errorData = await response.text();
            console.error("Work processor error response:", errorData);
            if (errorData) {
                errorMessage += ` - ${errorData}`;
            }
        } catch (e) {
            console.error("Could not parse error response");
        }
        throw new Error(errorMessage);
    }

    const rawResponse = await response.json();
    const jsonResponse: JobSubmitResponse = {
        job_id: String(rawResponse.job_id ?? rawResponse.jobid),
        message: String(rawResponse.message ?? "Job created successfully"),
        status: String(rawResponse.status ?? "ACCEPTED")
    };
    if (!jsonResponse.job_id || jsonResponse.job_id === "undefined") {
        throw new Error("Job submission response did not include a job id");
    }
    console.log("Job submission response:", jsonResponse);
    return jsonResponse;
}

/**
 * Get the status of a specific job
 */
export async function getJobStatus(jobId: string, idToken: string): Promise<JobStatus> {
    const url = `${BACKEND_URI}/api/jobprocessor/status/${encodeURIComponent(jobId)}`;
    const response = await fetchWithAuth(url, idToken, {
        method: "GET"
    });

    if (!response.ok) {
        if (response.status === 404) {
            throw new Error("Job not found");
        }
        throw new Error(`Failed to get job status: ${response.statusText}`);
    }

    return await response.json();
}

/**
 * Get status of a job by work product ID
 */
export async function getJobStatusByWorkProduct(workProductId: number, projectId: number, idToken: string): Promise<JobStatus> {
    const url = getWorkProcessorUrl(`/api/jobprocessor/status/workproduct/${workProductId}?projectid=${projectId}`);
    const response = await fetchWithAuth(url, idToken, {
        method: "GET"
    });

    if (!response.ok) {
        if (response.status === 404) {
            throw new Error("No job found for this work product");
        }
        throw new Error(`Failed to get job status: ${response.statusText}`);
    }

    return await response.json();
}

/**
 * Get all jobs for a specific project
 */
export async function getProjectJobs(projectId: number, idToken: string): Promise<JobStatus[]> {
    const url = getWorkProcessorUrl(`/api/jobprocessor/project/${projectId}/jobs`);
    const response = await fetchWithAuth(url, idToken, {
        method: "GET"
    });

    if (!response.ok) {
        throw new Error(`Failed to get project jobs: ${response.statusText}`);
    }

    return await response.json();
}

/**
 * Cancel a running job
 */
export async function cancelJob(jobId: string, idToken: string): Promise<{ message: string }> {
    const url = getWorkProcessorUrl(`/api/jobprocessor/cancel/${jobId}`);
    const response = await fetchWithAuth(url, idToken, {
        method: "POST"
    });

    if (!response.ok) {
        throw new Error(`Failed to cancel job: ${response.statusText}`);
    }

    return await response.json();
}

/**
 * Retry a failed job
 */
export async function retryJob(jobId: string, idToken: string): Promise<{ jobid: string; message: string }> {
    const url = getWorkProcessorUrl(`/api/jobprocessor/retry/${jobId}`);
    const response = await fetchWithAuth(url, idToken, {
        method: "POST"
    });

    if (!response.ok) {
        throw new Error(`Failed to retry job: ${response.statusText}`);
    }

    return await response.json();
}

/**
 * Poll job status updates for a specific job ID
 *
 * This function now uses the centralized JobStatusManager for efficient
 * batch polling when multiple jobs are running simultaneously.
 */
export async function streamJobUpdates(
    jobId: string,
    idToken: string,
    onUpdate: (data: JobStreamUpdate) => void,
    onComplete: (result: JobStreamUpdate) => void,
    onError: (error: Error) => void
): Promise<() => void> {
    console.log("Registering job for status tracking:", jobId);

    const callbacks: JobCallbacks = {
        onUpdate,
        onComplete,
        onError
    };

    // Register with the centralized job status manager
    const unregister = getJobStatusManager().registerJob(jobId, idToken, callbacks);

    // Return cleanup function
    return () => {
        console.log("Stopping status tracking for job:", jobId);
        unregister();
    };
}

/**
 * Submit a job and immediately start streaming its updates
 */
export async function submitAndStreamJob(
    jobData: JobRequest,
    idToken: string,
    onUpdate: (data: JobStreamUpdate) => void,
    onComplete: (result: JobStreamUpdate) => void,
    onError: (error: Error) => void
): Promise<{ jobId: string; cancelStream: () => void }> {
    try {
        // First submit the job
        const submitResponse = await submitJobToProcessor(jobData, idToken);
        console.log("Job submitted:", submitResponse);

        // Then start streaming the updates
        const cancelStream = await streamJobUpdates(submitResponse.job_id, idToken, onUpdate, onComplete, onError);

        return {
            jobId: submitResponse.job_id,
            cancelStream
        };
    } catch (error) {
        onError(error instanceof Error ? error : new Error("Failed to submit job"));
        return {
            jobId: "",
            cancelStream: () => {}
        };
    }
}

/**
 * Get batch status for multiple jobs at once
 *
 * This is useful for one-off batch status checks without using the
 * centralized polling manager.
 */
export async function getBatchJobStatus(
    jobIds: string[],
    idToken: string
): Promise<Record<string, JobStatus>> {
    const url = getWorkProcessorUrl("/api/jobs/status/batch");
    const merged: Record<string, JobStatus> = {};
    for (let offset = 0; offset < jobIds.length; offset += 100) {
        const chunk = jobIds.slice(offset, offset + 100);
        try {
            const response = await fetchWithAuth(url, idToken, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({ job_ids: chunk })
            });
            if (!response.ok) throw new Error(`Status ${response.status}`);
            const body = await response.json();
            if (body?.jobs && typeof body.jobs === "object") Object.assign(merged, body.jobs);
        } catch (error) {
            // The durable Intelligenic batch state remains authoritative for a
            // failed chunk; successful chunks can still refresh normally.
            console.warn("Work Product status chunk failed", { offset, size: chunk.length, error });
        }
    }
    return merged;
}

// Export the URL configuration for debugging/testing
export { WORK_PROCESSOR_API_URL };

// Re-export the JobStatusManager for direct access
export { getJobStatusManager } from "./jobStatusManager";
export type { JobCallbacks } from "./jobStatusManager";
